import { useCallback, useMemo, useRef } from "react";
import SearchResultItem from "./SearchResultItem";
import { usePaginatedSearchResults } from "../Sidebar/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { PaginatedChatsResponse } from "../Sidebar/constants";
import { formatGroupName, groupChatsByDate } from "../../helpers";
import { Typography } from "@mui/material";
import { ReactComponent as NewChat } from "../Icons/NewChat.svg";
import { ReactComponent as ChatMessage } from "../Icons/ChatMessage.svg";
import { mapSearchInputErrors, SearchInputErrors, SearchResultType } from "./constants";

type SearchResultsProps = {
  searchQuery: string;
  errorType: string;
};

const SearchResults = ({ searchQuery, errorType }: SearchResultsProps) => {
  const queryClient = useQueryClient();
  const cachedChats: PaginatedChatsResponse = queryClient.getQueryData(["chats"]);
  const groupedChats = useMemo(() => groupChatsByDate(cachedChats?.pages, 5), [cachedChats?.pages]);
  const isDefault = !searchQuery;
  const isSearchValid = !errorType && !!searchQuery;
  const shouldFetch = isSearchValid;

  // shouldFetch ensures that API isn't called if search term is not valid
  const { data, fetchNextPage, isFetching, hasNextPage, isError } = usePaginatedSearchResults({ query: searchQuery, enabled: shouldFetch });

  const resultCount = data?.pages[0].pagination["totalCount"] ?? null;

  const getMappedError = () => {
    if (errorType) {
      return mapSearchInputErrors[errorType];
    }
    else if (resultCount === 0 || isError) {
      return mapSearchInputErrors[SearchInputErrors.NO_RESULTS];
    }
    return null;
  };

  const mappedError = getMappedError();

  const searchContainerRef = useRef(null);

  const observerRef = useRef<IntersectionObserver | null>(null);

  const lastItemRef = useCallback((node: HTMLElement | null) => {
    if (isFetching) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    });

    if (node) observerRef.current.observe(node);
  }, [fetchNextPage, hasNextPage, isFetching]);

  return (
    <div className="px-2 pt-2 bg-white dark:bg-background/paper-elevation-6 rounded-lg h-[390px]">
      {mappedError && (
        <div className={`p-[12px] flex items-center gap-2 text-sm ${mappedError.classes}`}>
          <mappedError.icon className="w-5 h-5" />
          <Typography variant="body2/regular-400">{mappedError.message}</Typography>
        </div>
      )}

      {resultCount > 0 && (
        <div className="h-8">
          <Typography variant="overline/regular-400" className="ps-[12px] tracking-[1px] dark:text-mui-white-70">
            SHOWING
            {" "}
            {resultCount}
            {" "}
            CHATS
          </Typography>
        </div>
      )}
      <section className="h-[382px] pt-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
        {isDefault && (
          <div className="">
            <SearchResultItem title="New Chat" icon={<NewChat className="!fill-action-active" />} />
            {Object.entries(groupedChats).map(([groupName, chats]) => (
              chats.length > 0 && (
                <div key={groupName} className="pt-1">
                  <Typography variant="overline/regular-400" className="ps-[12px] tracking-[1px] dark:text-mui-white-70">
                    {formatGroupName(groupName)}
                  </Typography>
                  {chats.map((record, idx) => (
                    <div key={idx}>
                      <SearchResultItem title={record.title} sessionId={record.sessionId} icon={<ChatMessage className="!text-action-active !w-4 !h-4" />} />
                    </div>
                  ),
                  )}
                </div>
              )
            ))}
          </div>
        )}

        <div className="max-h-[346px] py-1 overflow-y-auto custom-scrollbar" ref={searchContainerRef}>
          {data?.pages.map(page =>
            page?.data.map((result: SearchResultType, index) => {
              const isLast = index === page?.data.length - 1;
              return (
                <SearchResultItem
                  key={result.session_id}
                  searchResult={result}
                  icon={<ChatMessage className="!text-action-active !w-4 !h-4" />}
                  resultRef={isLast ? lastItemRef : null}
                  searchedTerm={searchQuery}
                />
              );
            }),
          )}
          {isFetching && !errorType && Array.from({ length: 5 }).map((_, index) => (
            <SearchResultItem
              key={index}
              isLoading={true}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default SearchResults;
